import React from "react";
import { Select } from "@/components/Selects/select";
import {
  MultiValue,
  SingleValue,
  type ClassNamesConfig,
  type MenuPlacement,
} from "react-select";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { PracticeSetting } from "@api/shared-runtime/enums";

type OptionType = { label: string; value: PracticeSetting };

export const PracticeSettingSelect = ({
  value,
  onChange,
  placeholder = "No preference (open to any)",
  disabled = false,
  isMulti = true,
  classNames,
  menuPlacement,
  inputId,
}: {
  value?: PracticeSetting[];
  onChange?: (value: PracticeSetting[]) => void;
  placeholder?: string;
  disabled?: boolean;
  isMulti?: boolean;
  classNames?: ClassNamesConfig<OptionType | undefined, boolean>;
  menuPlacement?: MenuPlacement;
  inputId?: string;
}) => {
  const trpc = useTRPC();
  const { data, isLoading } = useQuery(
    trpc.facility.practiceSettings.queryOptions(),
  );

  const options: OptionType[] =
    data?.practiceSettings?.map((setting) => ({
      label: setting.displayText,
      value: setting.id as PracticeSetting,
    })) ?? [];

  const handleChange = (
    option:
      | MultiValue<OptionType | undefined>
      | SingleValue<OptionType | undefined>
      | null,
  ) => {
    if (isMulti) {
      const multiOption = option as MultiValue<OptionType | undefined>;
      onChange?.(
        multiOption
          ?.map((o) => o?.value)
          .filter((v): v is PracticeSetting => v !== undefined) || [],
      );
    } else {
      const singleOption = option as SingleValue<OptionType | undefined>;
      onChange?.(singleOption?.value ? [singleOption.value] : []);
    }
  };

  const inputValue = value
    ?.map((v) => options.find((option) => option.value === v))
    .filter(Boolean);

  return (
    <Select
      inputId={inputId}
      value={inputValue}
      options={options}
      onChange={handleChange}
      isDisabled={disabled}
      isLoading={isLoading}
      placeholder={placeholder}
      isMulti={isMulti}
      isClearable
      menuPlacement={menuPlacement}
      classNames={classNames}
    />
  );
};
